/**
 * What the tenant picked in the wizard's first three steps — operations,
 * machines and materials, where they ship and to whom (KAN-1473).
 *
 * The state used to be ten ``useState`` calls inside ``OnboardingStart``. It
 * lives here so the same steps can be mounted where there is no tenant: the
 * platform landing's pre-registration walks them over a public catalog, and
 * what it collects is this selection in the shape of
 * ``POST /admin/onboarding/start`` — see {@link toStartBody}.
 *
 * The hook fetches nothing. The catalog, the carriers and what „Přidat vlastní
 * operaci" may offer are handed in, because WHERE they come from is exactly
 * what differs between the tenant wizard and the anonymous one.
 */

import { useMemo, useState } from "react";
import type { TFunction } from "i18next";

import type { BlockerRule } from "@/components/ui/SubmitGuard";

import type { WizardDraft } from "./draft";
import {
  axisValuesOf,
  chainKey,
  CUSTOM,
  customMachineLabel,
  effectiveAxisRange,
  customWizardOperation,
  isCustomOperation,
  newMachineEntry,
  withOfferedMaterials,
  type Buyers,
  type CustomOperationDraft,
  type CustomOperationOptions,
  type OperationChoice,
  type WizardCarrier,
  type WizardCatalog,
  type WizardOperation,
} from "./model";

export const toggleIn = (list: string[], value: string): string[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

export function useWizardSelection({
  catalog,
  customOptions,
}: {
  catalog: WizardCatalog | null;
  customOptions: CustomOperationOptions | null;
}) {
  const [selectedOps, setSelectedOps] = useState<string[]>([]);
  // „Začít s prázdným obchodem" (KAN-1321) — vědomá volba, ne přeskočení.
  const [startEmpty, setStartEmpty] = useState(false);
  // „Přidat vlastní operaci" (KAN-1320): the operations named so far and the
  // open inline form.
  const [customOps, setCustomOps] = useState<CustomOperationDraft[]>([]);
  const [customForm, setCustomForm] = useState<CustomOperationDraft | null>(null);
  const [choices, setChoices] = useState<Record<string, OperationChoice>>({});
  const [countryCodes, setCountryCodes] = useState<string[]>([]);
  const [carrierCodes, setCarrierCodes] = useState<string[]>([]);
  const [buyers, setBuyers] = useState<Buyers | null>(null);
  // KAN-1825: „Nabízet osobní odběr" — jen zapíná, nikdy nevypíná.
  const [personalPickup, setPersonalPickup] = useState(false);
  // KAN-2159: pairs the tenant confirmed, by ``chainKey``. Nothing is
  // confirmed up front — the owner wants the tenant to tick it knowingly.
  const [confirmedChains, setConfirmedChains] = useState<string[]>([]);

  // Its own memo: a fresh ``[]`` on every render would make the memo
  // below recompute every time, which eslint names outright.
  const catalogOperations = useMemo(() => catalog?.operations ?? [], [catalog]);
  const operations = useMemo(
    () => [
      ...catalogOperations,
      ...customOps.map((draft) => customWizardOperation(draft, customOptions)),
    ],
    [catalogOperations, customOps, customOptions],
  );
  const chosen = useMemo(
    () => operations.filter((op) => selectedOps.includes(op.slug)),
    [operations, selectedOps],
  );

  const toggleOperation = (op: WizardOperation) => {
    setStartEmpty(false);
    setSelectedOps((current) =>
      current.includes(op.slug)
        ? current.filter((slug) => slug !== op.slug)
        : [...current, op.slug],
    );
    setChoices((current) => {
      if (current[op.slug]) return current;
      // KAN-1343: nothing is picked for the tenant. A brand-new shop opened
      // with the catalog's first printer and every material ticked — the
      // owner: „úplně nový obchod nesmí mít hned vybranou tiskárnu a
      // materiály". The step shows its empty state and „Pokračovat" asks.
      return { ...current, [op.slug]: { machines: [], materialCodes: [] } };
    });
  };

  const addCustomOperation = () => {
    if (!customForm || !customForm.name.trim()) return;
    const draft = {
      ...customForm,
      name: customForm.name.trim(),
      description: customForm.description.trim(),
    };
    setCustomOps((current) => [...current, draft]);
    toggleOperation(customWizardOperation(draft, customOptions));
    setCustomForm(null);
  };

  const removeCustomOperation = (key: string) => {
    setCustomOps((current) => current.filter((draft) => draft.key !== key));
    setSelectedOps((current) => current.filter((slug) => slug !== key));
  };

  const patchChoice = (slug: string, patch: Partial<OperationChoice>) =>
    setChoices((current) => ({
      ...current,
      [slug]: { ...current[slug], ...patch },
    }));

  // „Firmy" and „Koncoví zákazníci" are two ticks in the design and one
  // answer on the wire: both ticked is ``both``, neither is unanswered.
  const buyerOn = (side: "b2b" | "b2c") => buyers === side || buyers === "both";
  const toggleBuyer = (side: "b2b" | "b2c") => {
    const other = side === "b2b" ? "b2c" : "b2b";
    const next = { [side]: !buyerOn(side), [other]: buyerOn(other) } as Record<
      "b2b" | "b2c",
      boolean
    >;
    setBuyers(
      next.b2b && next.b2c ? "both" : next.b2b ? "b2b" : next.b2c ? "b2c" : null,
    );
  };

  /**
   * Take a stored draft back, THROUGH today's offer (KAN-1474).
   *
   * A draft is written against the catalog of the day it was made. The seeder
   * refuses an unknown operation, machine or material outright, so nothing the
   * catalog no longer offers may survive into the selection: it is dropped
   * here and NAMED in the return value, so the page can say what went missing
   * instead of quietly building a different shop than the one described.
   */
  const restore = (draft: WizardDraft, offer: WizardCatalog): string[] => {
    const dropped: string[] = [];
    const nextSelected: string[] = [];
    const nextChoices: Record<string, OperationChoice> = {};
    for (const entry of draft.operations) {
      const op = offer.operations.find((candidate) => candidate.slug === entry.slug);
      if (!op) {
        dropped.push(entry.slug);
        continue;
      }
      const addonKeys = new Set((op.addons ?? []).map((addon) => addon.machine_key));
      const machines = entry.machines.flatMap((machine) => {
        if (!op.machines.some((m) => m.machine_key === machine.catalog_key)) {
          dropped.push(machine.catalog_key);
          return [];
        }
        return [
          {
            ...newMachineEntry(op, machine.catalog_key),
            addonKeys: machine.addon_keys.filter((key) => addonKeys.has(key)),
            quantity: machine.quantity,
          },
        ];
      });
      const choice = withOfferedMaterials(op, { machines, materialCodes: entry.material_codes });
      dropped.push(...entry.material_codes.filter((code) => !choice.materialCodes.includes(code)));
      nextSelected.push(op.slug);
      nextChoices[op.slug] = choice;
    }
    const countries = new Set(offer.countries.map((c) => c.country_code));
    dropped.push(...draft.country_codes.filter((code) => !countries.has(code)));
    setStartEmpty(false);
    setCustomOps([]);
    setCustomForm(null);
    setSelectedOps(nextSelected);
    setChoices(nextChoices);
    setCountryCodes(draft.country_codes.filter((code) => countries.has(code)));
    // Carriers depend on the countries; the page drops the ones the carrier
    // read no longer offers, as it does for every change of country.
    setCarrierCodes(draft.carrier_codes);
    setBuyers(draft.buyers);
    return dropped;
  };

  const machineCount = chosen.reduce(
    (n, op) =>
      n +
      (choices[op.slug]?.machines
        .filter((e) => e.machineKey !== "")
        .reduce((sum, e) => sum + e.quantity, 0) ?? 0),
    0,
  );
  const materialCount = chosen.reduce(
    (n, op) => n + (choices[op.slug]?.materialCodes.length ?? 0),
    0,
  );

  // KAN-1501 — panel „Vaše volby" umí u krátkého výběru vypsat jména
  // místo počtu, takže je potřebuje dostat. Počty zůstávají: nad čtyřmi
  // položkami se pořád vypisuje číslo, a ``machineCount`` navíc sčítá
  // KUSY (jeden řádek může být „3× tentýž stroj"), kdežto jména jsou
  // po řádcích. Dvě různé otázky, dvě hodnoty.
  //
  // Ručně založený stroj má jméno až ve chvíli, kdy tenant vyplní
  // výrobce a model; do té doby se ze seznamu vynechá, ať tam
  // nesvítí prázdná čárka.
  const machineNames = chosen.flatMap((op) =>
    (choices[op.slug]?.machines ?? [])
      .filter((e) => e.machineKey !== "")
      .map((e) =>
        e.machineKey === CUSTOM
          ? customMachineLabel(e)
          : (op.machines.find((m) => m.machine_key === e.machineKey)?.name ?? ""),
      )
      .filter((name) => name !== ""),
  );
  const materialNames = chosen.flatMap((op) =>
    (choices[op.slug]?.materialCodes ?? []).map(
      (code) =>
        op.materials.find((m) => m.code === code)?.display_name ?? code,
    ),
  );

  // Only pairs whose BOTH operations are picked; un-picking one drops the
  // question, and ``toStartBody`` drops a confirmation left behind by it.
  const proposedChains = useMemo(
    () =>
      (catalog?.chains ?? []).filter(
        (chain) =>
          selectedOps.includes(chain.source) &&
          selectedOps.includes(chain.target),
      ),
    [catalog, selectedOps],
  );
  const toggleChain = (key: string) =>
    setConfirmedChains((current) => toggleIn(current, key));

  return {
    selectedOps,
    setSelectedOps,
    proposedChains,
    confirmedChains,
    toggleChain,
    startEmpty,
    setStartEmpty,
    customOps,
    setCustomOps,
    customForm,
    setCustomForm,
    choices,
    countryCodes,
    setCountryCodes,
    carrierCodes,
    setCarrierCodes,
    buyers,
    personalPickup,
    setPersonalPickup,
    catalogOperations,
    chosen,
    toggleOperation,
    addCustomOperation,
    removeCustomOperation,
    patchChoice,
    buyerOn,
    toggleBuyer,
    restore,
    machineCount,
    materialCount,
    machineNames,
    materialNames,
  };
}

export type WizardSelection = ReturnType<typeof useWizardSelection>;

/**
 * Rules for one of the first three steps, in the ``condition && "message"``
 * shape ``useSubmitGuard`` takes. The buttons stay CLICKABLE and the rules
 * run on click — a button greyed out for an unfilled field tells the
 * tenant it does not work but never why, which is the dead end
 * ``SubmitGuard``'s own docstring exists to argue against.
 */
export function selectionRules(
  current: string,
  w: WizardSelection,
  carriers: WizardCarrier[],
  t: TFunction<"admin">,
): BlockerRule[] {
  if (current === "operations") {
    // KAN-1130: the step had NO rule, so „Pokračovat" walked a tenant
    // who picked nothing through the whole wizard and posted
    // ``operations: []`` — a finished wizard and an empty shop. The
    // backend accepts that body on purpose (it is what „Přeskočit"
    // records), which is exactly why the question has to be asked here.
    // KAN-1321: „Začít s prázdným obchodem" je odpověď na tutéž otázku.
    return [
      w.chosen.length === 0 &&
        !w.startEmpty &&
        t("onboardingStart.blockers.operations"),
    ];
  }
  if (current === "blocks") {
    return w.chosen.flatMap((op) => {
      const machines = w.choices[op.slug]?.machines ?? [];
      const materials = w.choices[op.slug]?.materialCodes ?? [];
      return [
        // KAN-1343: nothing is preselected any more, so an operation with no
        // machine or no material is an unanswered question, not a default.
        machines.length === 0 &&
          t("onboardingStart.blockers.machineMissing", {
            operation: op.display_name,
          }),
        machines.length > 0 &&
          op.materials.length > 0 &&
          materials.length === 0 &&
          t("onboardingStart.blockers.materialMissing", {
            operation: op.display_name,
          }),
        // KAN-1197: a row whose manufacturer was picked but not its model
        // would otherwise be dropped from the POST without a word.
        machines.some((entry) => entry.machineKey === "") &&
          t("onboardingStart.blockers.machineModel", {
            operation: op.display_name,
          }),
        machines.some(
          (entry) => entry.machineKey === CUSTOM && !customMachineLabel(entry),
        ) &&
          t("onboardingStart.blockers.machineName", {
            operation: op.display_name,
          }),
      ];
    });
  }
  if (current === "delivery") {
    return [
      w.countryCodes.length === 0 && t("onboardingStart.blockers.countries"),
      // KAN-1343: carriers are no longer ticked for the tenant. Asked only
      // when some are offered — none offered is its own honest empty state.
      carriers.length > 0 &&
        w.carrierCodes.length === 0 &&
        t("onboardingStart.blockers.carriers"),
      w.buyers === null && t("onboardingStart.blockers.buyers"),
    ];
  }
  return [];
}

const machinesBody = (w: WizardSelection, slug: string) =>
  (w.choices[slug]?.machines ?? []).map((entry) =>
    entry.machineKey === CUSTOM
      ? {
          ...(entry.customName ? { custom_name: entry.customName } : {}),
          custom_vendor: entry.customVendor.trim(),
          custom_model: entry.customModel.trim(),
          // KAN-1383 — a platform machine under an own operation processes
          // the operation's materials, as ticked at the moment of the build.
          custom_material_codes: entry.unverifiedKey
            ? (w.choices[slug]?.materialCodes ?? [])
            : entry.customMaterialCodes,
          custom_config: entry.customConfig,
          quantity: entry.quantity,
        }
      : {
          catalog_key: entry.machineKey,
          addon_keys: entry.addonKeys,
          quantity: entry.quantity,
        },
  );

/** The selection as the body of ``POST /admin/onboarding/start``. */
export function toStartBody(w: WizardSelection) {
  return {
    buyers: w.buyers,
    country_codes: w.countryCodes,
    carrier_codes: w.carrierCodes,
    start_empty: w.startEmpty,
    ...(w.personalPickup ? { personal_pickup: true } : {}),
    operations: w.chosen
      .filter((op) => !isCustomOperation(op.slug))
      .map((op) => {
        const choice = w.choices[op.slug];
        // KAN-2012 bod 6 — jen užší než vše nabízené; jinak seed bere všechno.
        const axisRange = effectiveAxisRange(
          axisValuesOf(op, choice.materialCodes),
          choice.axisRange,
        );
        return {
          slug: op.slug,
          machines: machinesBody(w, op.slug),
          material_codes: choice.materialCodes,
          ...(axisRange ? { axis_range: axisRange } : {}),
        };
      }),
    chains: w.proposedChains
      .filter((chain) => w.confirmedChains.includes(chainKey(chain)))
      .map(({ source, target }) => ({ source, target })),
    // KAN-1320: founded by the backend, then seeded like the rest.
    custom_operations: w.customOps
      .filter((draft) => w.selectedOps.includes(draft.key))
      .map((draft) => ({
        name: draft.name,
        description: draft.description || null,
        operation_category: draft.operationCategory,
        material_category: draft.materialCategory,
        machines: machinesBody(w, draft.key),
        material_codes: w.choices[draft.key]?.materialCodes ?? [],
      })),
  };
}
/**
 * The selection as a pre-registration draft: the start body without anything
 * own. An own operation or machine depends on the plan and on the tenant's own
 * categories, so it cannot be promised to a shop that does not exist yet.
 */
export function toWizardDraft(w: WizardSelection): WizardDraft {
  return {
    buyers: w.buyers,
    country_codes: w.countryCodes,
    carrier_codes: w.carrierCodes,
    operations: w.chosen
      .filter((op) => !isCustomOperation(op.slug))
      .map((op) => ({
        slug: op.slug,
        machines: (w.choices[op.slug]?.machines ?? [])
          .filter((entry) => entry.machineKey !== "" && entry.machineKey !== CUSTOM)
          .map((entry) => ({
            catalog_key: entry.machineKey,
            addon_keys: entry.addonKeys,
            quantity: entry.quantity,
          })),
        material_codes: w.choices[op.slug]?.materialCodes ?? [],
      })),
  };
}
